/**
 * StatCard Component
 * Standardized metric/statistic card with variants
 */

import React from 'react';
import { Card } from './Card';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { typography } from '../../styles/typography';

export interface StatCardProps {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  subtitle?: string;
  colorScheme?: 'positive' | 'warning' | 'negative' | 'info' | 'neutral';
  variant?: 'simple' | 'gradient';
  className?: string;
}

const colorSchemes = {
  positive: {
    simple: {
      value: 'text-stat-positive',
      icon: 'text-success-main',
      bg: '',
    },
    gradient: {
      value: 'text-success-dark dark:text-success-light',
      icon: 'text-success-main',
      bg: 'bg-gradient-to-br from-success-light/20 to-success-main/10 border-success-main/20',
    },
  },
  warning: {
    simple: {
      value: 'text-stat-warning',
      icon: 'text-warning-main',
      bg: '',
    },
    gradient: {
      value: 'text-warning-dark dark:text-warning-light',
      icon: 'text-warning-main',
      bg: 'bg-gradient-to-br from-warning-light/20 to-warning-main/10 border-warning-main/20',
    },
  },
  negative: {
    simple: {
      value: 'text-stat-negative',
      icon: 'text-error-main',
      bg: '',
    },
    gradient: {
      value: 'text-error-dark dark:text-error-light',
      icon: 'text-error-main',
      bg: 'bg-gradient-to-br from-error-light/20 to-error-main/10 border-error-main/20',
    },
  },
  info: {
    simple: {
      value: 'text-stat-info',
      icon: 'text-info-main',
      bg: '',
    },
    gradient: {
      value: 'text-info-dark dark:text-info-light',
      icon: 'text-info-main',
      bg: 'bg-gradient-to-br from-info-light/20 to-info-main/10 border-info-main/20',
    },
  },
  neutral: {
    simple: {
      value: 'text-stat-neutral',
      icon: 'text-theme-text',
      bg: '',
    },
    gradient: {
      value: 'text-theme-text',
      icon: 'text-primary-600 dark:text-primary-400',
      bg: 'bg-gradient-to-br from-theme-elevated to-theme-surface border-theme-border-dark',
    },
  },
};

export function StatCard({
  label,
  value,
  icon,
  trend,
  subtitle,
  colorScheme = 'neutral',
  variant = 'simple',
  className = '',
}: StatCardProps) {
  const colors = colorSchemes[colorScheme][variant];
  const padding = variant === 'gradient' ? 'p-6' : 'p-4';
  
  return (
    <Card 
      className={`${padding} ${colors.bg} ${className}`}
      hover={variant === 'simple'}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className={typography.statLabel}>{label}</p>
          <div className="flex items-center gap-2 mt-1">
            <p className={`${typography.statValue} ${colors.value}`}>
              {value}
            </p>
            {trend && trend !== 'neutral' && (
              <span className={trend === 'up' ? 'text-success-main' : 'text-error-main'}>
                {trend === 'up' ? (
                  <TrendingUp className="h-5 w-5" />
                ) : (
                  <TrendingDown className="h-5 w-5" />
                )}
              </span>
            )}
          </div>
          {subtitle && (
            <p className={`${typography.caption} mt-1`}>
              {subtitle}
            </p>
          )}
        </div>
        
        {icon && (
          <div className={`h-8 w-8 ${colors.icon} flex-shrink-0 ml-4`}>
            {icon}
          </div>
        )}
      </div>
    </Card>
  );
}

